import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  IconAlertCircle, IconMail, IconLock, IconEye, IconEyeOff, IconShieldCheck,
} from '@tabler/icons-react';
import api from '../api/api';

const NAVER_CALLBACK_URL = `${window.location.origin}/auth/naver/callback`;
const KAKAO_CALLBACK_URL = `${window.location.origin}/auth/kakao/callback`;

const SAVE_KEY = 'ai65-user-saved-credentials';

export default function LoginPage({ setIsLoggedIn }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [siteConfig, setSiteConfig] = useState({ 'site.name': 'AI65 Neo', 'site.description': '', 'site.loginColor': 'blue' });

  const LOGIN_COLOR_GRADIENTS = {
    blue:   'linear-gradient(135deg, #206bc4 0%, #4263eb 50%, #ae3ec9 100%)',
    green:  'linear-gradient(135deg, #2d6a4f 0%, #52b788 100%)',
    red:    'linear-gradient(135deg, #8b2635 0%, #c0392b 100%)',
    purple: 'linear-gradient(135deg, #4a2c8a 0%, #6741d9 100%)',
    black:  'linear-gradient(135deg, #212529 0%, #495057 100%)',
    gray:   'linear-gradient(135deg, #868e96 0%, #adb5bd 100%)',
  };
  const LOGIN_COLOR_PRIMARY = {
    blue: '#206bc4', green: '#2d6a4f', red: '#8b2635',
    purple: '#4a2c8a', black: '#212529', gray: '#868e96',
  };
  const loginColor = siteConfig['site.loginColor'] || 'blue';
  const loginGradient = LOGIN_COLOR_GRADIENTS[loginColor] || LOGIN_COLOR_GRADIENTS.blue;
  const loginPrimary = LOGIN_COLOR_PRIMARY[loginColor] || LOGIN_COLOR_PRIMARY.blue;
  const [oauthConfig, setOauthConfig] = useState(null); // { google: { clientId }, naver: { clientId } }
  const [googleLinkModal, setGoogleLinkModal] = useState(null); // { email, token }
  const [linkPassword, setLinkPassword] = useState('');
  const [linkError, setLinkError] = useState('');
  const [linkLoading, setLinkLoading] = useState(false);

  // 네이버 콜백에서 넘어온 에러 메시지 처리
  useEffect(() => {
    // 로그인 페이지 진입 시 만료된 토큰 정리
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
  }, []);

  useEffect(() => {
    if (location.state?.error) {
      setError(location.state.error);
    }
  }, [location.state]);

  useEffect(() => {
    // OAuth config + Google Sign-In 초기화
    const initGoogleSignIn = (googleClientId) => {
      if (!window.google || !googleClientId) return false;
      try {
        window.google.accounts.id.initialize({
          client_id: googleClientId,
          callback: (response) => {
            if (response.credential) {
              handleGoogleLogin(response.credential);
            }
          },
        });
        const googleLoginDiv = document.getElementById('google-login-button');
        if (googleLoginDiv) {
          googleLoginDiv.innerHTML = '';
          window.google.accounts.id.renderButton(googleLoginDiv, {
            theme: 'outline', size: 'large', width: 360, text: 'signin_with',
          });
        }
        return true;
      } catch (e) {
        console.error('Google Sign-In 초기화 실패:', e);
        return false;
      }
    };

    // 백엔드에서 OAuth Client ID 가져오기
    api.get('/auth/oauth-config').then(res => {
      setOauthConfig(res.data);
      const googleClientId = res.data?.google?.clientId;
      if (!initGoogleSignIn(googleClientId)) {
        let retryCount = 0;
        const retryInterval = setInterval(() => {
          retryCount++;
          if (initGoogleSignIn(googleClientId) || retryCount >= 10) {
            clearInterval(retryInterval);
          }
        }, 500);
      }
    }).catch(() => {});

    api.get('/system-config').then(res => {
      setSiteConfig(prev => ({ ...prev, ...res.data }));
      if (res.data['site.favicon']) { let l = document.querySelector("link[rel~='icon']"); if (!l) { l = document.createElement('link'); l.rel = 'icon'; document.head.appendChild(l); } l.href = res.data['site.favicon']; }
      if (res.data['site.name']) document.title = res.data['site.name'];
    }).catch(() => {});
    try {
      const saved = JSON.parse(localStorage.getItem(SAVE_KEY));
      if (saved?.email) {
        setEmail(saved.email);
        setPassword(saved.password || '');
        setRemember(true);
      }
    } catch {}
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await api.post('/auth/login', { email, password });
      const { token } = res.data;

      if (remember) {
        localStorage.setItem(SAVE_KEY, JSON.stringify({ email, password }));
      } else {
        localStorage.removeItem(SAVE_KEY);
      }

      localStorage.setItem('token', token);
      if (res.data.refreshToken) localStorage.setItem('refreshToken', res.data.refreshToken);
      if (res.data.mustChangePassword) {
        navigate('/change-password', { state: { forced: true } });
        return;
      }
      setIsLoggedIn(true);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.message || '이메일 또는 비밀번호가 올바르지 않습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleKakaoLogin = () => {
    const kakaoClientId = oauthConfig?.kakao?.clientId;
    if (!kakaoClientId) { setError('카카오 로그인 설정을 불러오는 중입니다. 잠시 후 다시 시도하세요.'); return; }
    const kakaoAuthUrl = `https://kauth.kakao.com/oauth/authorize?response_type=code&client_id=${kakaoClientId}&redirect_uri=${encodeURIComponent(KAKAO_CALLBACK_URL)}`;
    window.location.href = kakaoAuthUrl;
  };

  const handleGoogleLogin = async (credential) => {
    setError('');
    setLoading(true);
    try {
      const res = await api.post('/auth/google-login', { token: credential });
      localStorage.setItem('token', res.data.token);
      if (res.data.refreshToken) localStorage.setItem('refreshToken', res.data.refreshToken);
      setIsLoggedIn(true);
      navigate('/');
    } catch (err) {
      if (err.response?.data?.code === 'ACCOUNT_EXISTS') {
        setGoogleLinkModal({
          email: err.response.data.email,
          token: err.response.data.socialToken,
        });
        setLoading(false);
      } else {
        setError(err.response?.data?.message || 'Google 로그인에 실패했습니다.');
        setLoading(false);
      }
    }
  };

  const handleNaverLogin = () => {
    const naverClientId = oauthConfig?.naver?.clientId;
    if (!naverClientId) { setError('네이버 로그인 설정을 불러오는 중입니다. 잠시 후 다시 시도하세요.'); return; }
    const state = Math.random().toString(36).substring(2, 15);
    sessionStorage.setItem('naver_oauth_state', state);
    const naverAuthUrl = `https://nid.naver.com/oauth2.0/authorize?response_type=code&client_id=${naverClientId}&redirect_uri=${encodeURIComponent(NAVER_CALLBACK_URL)}&state=${state}`;
    window.location.href = naverAuthUrl;
  };

  const handleGoogleLink = async (e) => {
    e.preventDefault();
    if (!linkPassword.trim()) { setLinkError('비밀번호를 입력하세요.'); return; }
    setLinkError('');
    setLinkLoading(true);
    try {
      const res = await api.post('/auth/google-login', {
        token: googleLinkModal.token,
        password: linkPassword,
      });
      localStorage.setItem('token', res.data.token);
      if (res.data.refreshToken) localStorage.setItem('refreshToken', res.data.refreshToken);
      setIsLoggedIn(true);
      navigate('/');
    } catch (err) {
      setLinkError(err.response?.data?.message || '계정 연결에 실패했습니다.');
      setLinkLoading(false);
    }
  };

  const inputStyle = {
    paddingLeft: 38, height: 44, borderRadius: 10, fontSize: 14,
    border: '1.5px solid var(--tblr-border-color, #e6e7e9)',
    transition: 'border-color 0.15s',
  };

  // Google 계정 연결 모달
  if (googleLinkModal) {
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'var(--tblr-bg-surface, #f4f6fa)',
      }}>
        <div style={{
          background: '#fff', borderRadius: 16, padding: '40px 36px',
          maxWidth: 420, width: '100%',
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
        }}>
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <div style={{
              width: 56, height: 56, borderRadius: 14,
              background: '#fff', border: '2px solid #e6e7e9',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 16px',
            }}>
              <svg width={28} height={28} viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
            </div>
            <h3 style={{ fontWeight: 700, fontSize: 20, marginBottom: 8 }}>기존 계정 연결</h3>
            <p style={{ fontSize: 14, color: '#868e96', lineHeight: 1.6 }}>
              <strong>{googleLinkModal.email}</strong> 으로 가입된 계정이 있습니다.<br />
              <strong>사이트 계정 비밀번호</strong>를 입력하면 Google과 연결됩니다.
            </p>
          </div>

          {linkError && (
            <div className="alert alert-danger mb-3" style={{ borderRadius: 10, border: 'none', background: '#fff0f0', fontSize: 14 }}>
              <div className="d-flex align-items-center gap-2">
                <IconAlertCircle size={16} style={{ color: '#d63939', flexShrink: 0 }} />
                <span>{linkError}</span>
              </div>
            </div>
          )}

          <form onSubmit={handleGoogleLink}>
            <div className="mb-3">
              <label className="form-label fw-medium" style={{ fontSize: 14 }}>비밀번호</label>
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--tblr-muted)', pointerEvents: 'none' }}>
                  <IconLock size={17} />
                </span>
                <input type="password" className="form-control" placeholder="기존 계정 비밀번호"
                  value={linkPassword} onChange={e => setLinkPassword(e.target.value)}
                  autoFocus required style={inputStyle} />
              </div>
            </div>
            <button type="submit" disabled={linkLoading} style={{
              width: '100%', height: 44, borderRadius: 10, fontSize: 15, fontWeight: 600,
              background: linkLoading ? 'var(--tblr-muted)' : '#4285F4',
              border: 'none', color: '#fff', cursor: linkLoading ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            }}>
              {linkLoading ? (<><span className="spinner-border spinner-border-sm" style={{ width: 16, height: 16 }} /> 연결 중...</>) : '계정 연결'}
            </button>
            <button type="button" onClick={() => { setGoogleLinkModal(null); setLinkPassword(''); setLinkError(''); }} style={{
              width: '100%', height: 44, borderRadius: 10, fontSize: 14,
              background: 'transparent', border: '1px solid var(--tblr-border-color, #e6e7e9)',
              color: 'var(--tblr-muted)', cursor: 'pointer', marginTop: 8,
            }}>취소</button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', background: 'var(--tblr-bg-surface, #f4f6fa)' }}>
      {/* 왼쪽 브랜딩 */}
      <div style={{
        flex: 1,
        background: loginGradient,
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        padding: 48, color: '#fff', minWidth: 0,
      }} className="d-none d-lg-flex">
        <div style={{ textAlign: 'center', maxWidth: 400 }}>
          {siteConfig['site.logo'] ? (
            <img src={siteConfig['site.logo']} alt="로고"
              style={{ width: '100%', maxWidth: 300, maxHeight: 100, borderRadius: 20, objectFit: 'contain', margin: '0 auto 24px', display: 'block' }} />
          ) : (
            <div style={{
              width: 80, height: 80, borderRadius: 20,
              background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(10px)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 24px', fontSize: 36, fontWeight: 900,
              border: '2px solid rgba(255,255,255,0.3)',
            }}>{siteConfig['site.name']?.[0] || 'A'}</div>
          )}
          <h1 style={{ fontSize: 36, fontWeight: 800, marginBottom: 12 }}>{siteConfig['site.name']}</h1>
          <p style={{ fontSize: 16, opacity: 0.85, lineHeight: 1.7, marginBottom: 40, whiteSpace: 'pre-line' }}>
            {siteConfig['site.description'] || `${siteConfig['site.name']}에 오신 것을 환영합니다.\n게시판, 일정, 쪽지 등 다양한 서비스를 이용하세요.`}
          </p>
          {['커뮤니티 게시판', '일정 관리', '쪽지 서비스'].map((text, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, textAlign: 'left' }}>
              <div style={{
                width: 24, height: 24, borderRadius: '50%',
                background: 'rgba(255,255,255,0.25)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}><IconShieldCheck size={14} /></div>
              <span style={{ fontSize: 14, opacity: 0.9 }}>{text}</span>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 'auto', opacity: 0.6, fontSize: 13 }}>
          &copy; {new Date().getFullYear()} {siteConfig['site.name']}. All rights reserved.
        </div>
      </div>

      {/* 오른쪽 로그인 폼 */}
      <div style={{
        width: '100%', maxWidth: 480,
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        padding: '48px 40px', background: 'var(--tblr-bg-surface, #fff)',
      }}>
        <div className="d-lg-none text-center mb-4">
          <span style={{ fontSize: 28, fontWeight: 800, color: 'var(--tblr-primary, #206bc4)' }}>{siteConfig['site.name']}</span>
        </div>

        <div style={{ width: '100%', maxWidth: 360 }}>
          <div className="mb-4">
            <h2 style={{ fontWeight: 700, fontSize: 24, marginBottom: 4 }}>로그인</h2>
            <p className="text-muted" style={{ fontSize: 14 }}>계정에 로그인하세요.</p>
          </div>

          {error && (
            <div className="alert alert-danger mb-4" style={{ borderRadius: 10, border: 'none', background: '#fff0f0' }}>
              <div className="d-flex align-items-center gap-2">
                <IconAlertCircle size={18} style={{ color: '#d63939', flexShrink: 0 }} />
                <span style={{ fontSize: 14 }}>{error}</span>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="mb-3">
              <label className="form-label fw-medium" style={{ fontSize: 14 }}>아이디</label>
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--tblr-muted)', pointerEvents: 'none' }}>
                  <IconMail size={17} />
                </span>
                <input type="text" className="form-control" placeholder="아이디를 입력하세요"
                  value={email} onChange={e => setEmail(e.target.value)} required autoFocus style={inputStyle}
                  onFocus={e => e.target.style.borderColor = 'var(--tblr-primary)'}
                  onBlur={e => e.target.style.borderColor = 'var(--tblr-border-color, #e6e7e9)'} />
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label fw-medium" style={{ fontSize: 14 }}>비밀번호</label>
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--tblr-muted)', pointerEvents: 'none' }}>
                  <IconLock size={17} />
                </span>
                <input type={showPw ? 'text' : 'password'} className="form-control" placeholder="비밀번호를 입력하세요"
                  value={password} onChange={e => setPassword(e.target.value)} required
                  style={{ ...inputStyle, paddingRight: 40 }}
                  onFocus={e => e.target.style.borderColor = 'var(--tblr-primary)'}
                  onBlur={e => e.target.style.borderColor = 'var(--tblr-border-color, #e6e7e9)'} />
                <button type="button" onClick={() => setShowPw(v => !v)}
                  style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: 'var(--tblr-muted)' }}
                  tabIndex={-1}>
                  {showPw ? <IconEyeOff size={17} /> : <IconEye size={17} />}
                </button>
              </div>
            </div>

            <div className="mb-4">
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none' }}>
                <input type="checkbox" className="form-check-input m-0" checked={remember}
                  onChange={e => setRemember(e.target.checked)} style={{ width: 17, height: 17, borderRadius: 4, cursor: 'pointer' }} />
                <span style={{ fontSize: 14 }}>로그인 정보 저장</span>
              </label>
            </div>

            <button type="submit" disabled={loading} style={{
              width: '100%', height: 46, borderRadius: 10, fontSize: 15, fontWeight: 600,
              background: loading ? 'var(--tblr-muted)' : loginPrimary,
              border: 'none', color: '#fff', cursor: loading ? 'not-allowed' : 'pointer',
              boxShadow: loading ? 'none' : '0 4px 12px rgba(32,107,196,0.35)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            }}>
              {loading ? (<><span className="spinner-border spinner-border-sm" style={{ width: 18, height: 18 }} /> 로그인 중...</>) : '로그인'}
            </button>

            {/* 소셜 로그인 (Kakao는 임시 비활성화) */}
            <div style={{ marginTop: 16, position: 'relative' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                <div style={{ flex: 1, height: '1px', background: 'var(--tblr-border-color, #e6e7e9)' }} />
                <span style={{ fontSize: 13, color: 'var(--tblr-muted)', whiteSpace: 'nowrap' }}>또는</span>
                <div style={{ flex: 1, height: '1px', background: 'var(--tblr-border-color, #e6e7e9)' }} />
              </div>

              {/* Google 로그인 버튼 */}
              {oauthConfig?.google && (
                <div id="google-login-button" style={{ marginBottom: 12 }} />
              )}

              {/* Naver 로그인 버튼 */}
              {oauthConfig?.naver && (
                <button type="button" onClick={handleNaverLogin} disabled={loading} style={{
                  width: '100%', height: 46, borderRadius: 10, fontSize: 15, fontWeight: 600,
                  background: '#03C75A', color: '#fff', border: 'none',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  boxShadow: '0 2px 8px rgba(3,199,90,0.3)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  opacity: loading ? 0.6 : 1, marginBottom: 12,
                }}>
                  <svg width={18} height={18} viewBox="0 0 24 24" fill="currentColor">
                    <path d="M16.273 12.845L7.376 0H0v24h7.727V11.155L16.624 24H24V0h-7.727z"/>
                  </svg>
                  네이버로 로그인
                </button>
              )}

              {/* Kakao 로그인 */}
              {oauthConfig?.kakao && (
                <button type="button" onClick={handleKakaoLogin} disabled={loading} style={{
                  width: '100%', height: 46, borderRadius: 10, fontSize: 15, fontWeight: 600,
                  background: '#FFE812', color: '#3C1E1E', border: 'none',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  boxShadow: '0 2px 8px rgba(255,232,18,0.3)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  opacity: loading ? 0.6 : 1,
                }}>
                  <svg width={18} height={18} viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 3C6.48 3 2 6.58 2 10.94c0 2.8 1.86 5.27 4.66 6.68l-1.18 4.38 5.09-3.35c.47.05.94.08 1.43.08 5.52 0 10-3.58 10-7.79S17.52 3 12 3z"/>
                  </svg>
                  카카오로 로그인
                </button>
              )}
            </div>
          </form>

          <div className="text-center mt-3" style={{ fontSize: 13 }}>
            <span className="text-muted" style={{ cursor: 'pointer' }}
              onClick={() => alert('비밀번호를 잊으셨다면 관리자에게 문의하세요.\n관리자가 임시 비밀번호를 발급해 드립니다.')}>
              비밀번호를 잊으셨나요?
            </span>
          </div>

          {siteConfig['site.signupEnabled'] !== 'false' && (
            <div className="text-center mt-2" style={{ fontSize: 14 }}>
              <span className="text-muted">계정이 없으신가요? </span>
              <a href="/signup" style={{ color: loginPrimary, fontWeight: 600, textDecoration: 'none' }}
                onClick={e => { e.preventDefault(); navigate('/signup'); }}>
                회원가입
              </a>
            </div>
          )}
        </div>

        <div className="d-lg-none mt-auto pt-4 text-muted" style={{ fontSize: 12 }}>
          &copy; {new Date().getFullYear()} {siteConfig['site.name']}
        </div>
      </div>
    </div>
  );
}
